import React, { useState, useEffect, useCallback, useRef } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Pagination from '@amzn/meridian/pagination';
import SearchField from '@amzn/meridian/search-field';
import { getNodeConfig, uploadNodeConfig } from '../services/api';
import { NODE_CONFIG_TABLE, getCurrentUser } from '../appConfig';
import { parseCSV, downloadCSV } from '../utils/csvUtils';

const REQUIRED_CSV_COLUMNS = [
    'location_id', 'ship_method', 'cpt', 'type', 'parent', 'region', 'cycle_name', 'sort_code', 'node_type',
];

const COLUMNS = [
    { id: 'location_id', label: 'Node' },
    { id: 'parent', label: 'Parent' },
    { id: 'region', label: 'Region' },
    { id: 'cycle_name', label: 'Cycle' },
    { id: 'sort_code', label: 'Sort Code' },
    { id: 'cpt', label: 'CPT' },
    { id: 'ship_method', label: 'Ship Method' },
    { id: 'type', label: 'Type' },
    { id: 'node_type', label: 'Node Type' },
];

const NodeConfig: React.FC = () => {
    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [resultsPerPage] = useState(20);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getNodeConfig(NODE_CONFIG_TABLE);
            const items = Array.isArray(result?.items || result) ? (result?.items || result) : [];
            setData(items);
        } catch (err: any) {
            setError(err.message || 'Failed to load node config');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset the input so the same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';

        setError(null);
        setSuccess(null);
        setWarning(null);

        if (!file.name.endsWith('.csv')) {
            setError('Please select a CSV file');
            return;
        }

        try {
            // First read as text to validate columns
            const csvText = await new Promise<string>((resolve, reject) => {
                const textReader = new FileReader();
                textReader.onload = () => resolve(textReader.result as string);
                textReader.onerror = () => reject(new Error('Failed to read file'));
                textReader.readAsText(file);
            });

            const firstLine = csvText.split('\n')[0] || '';
            const headers = firstLine.split(',').map(h => h.trim().toLowerCase().replace(/["\r]/g, ''));
            const missingColumns = REQUIRED_CSV_COLUMNS.filter(col => !headers.includes(col));

            if (missingColumns.length > 0) {
                setError(`CSV is missing required column(s): ${missingColumns.join(', ')}`);
                return;
            }

            setIsUploading(true);

            // Read as base64 for upload
            const fileContent = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    // Extract base64 content from data URL
                    const base64 = result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = () => reject(new Error('Failed to read file'));
                reader.readAsDataURL(file);
            });

            const user = getCurrentUser();
            const response = await uploadNodeConfig(fileContent, user);

            const itemsWritten = response?.items_written || 0;
            const itemsDeleted = response?.items_deleted || 0;
            const skippedRecords: string[] = response?.skipped_records || [];

            setSuccess(
                `Successfully uploaded ${file.name}: ${itemsWritten} items written, ${itemsDeleted} old items deleted.`
            );

            if (skippedRecords.length > 0) {
                setWarning(
                    `${skippedRecords.length} duplicate record(s) skipped:\n${skippedRecords.join('\n')}`
                );
            }

            await loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to upload node config CSV');
        } finally {
            setIsUploading(false);
        }
    }, [loadData]);

    const handleDownload = useCallback(() => {
        if (data.length === 0) return;
        const csvColumns = REQUIRED_CSV_COLUMNS;
        const rows = data.map(item =>
            csvColumns.map(col => String(item[col] || ''))
        );
        downloadCSV('node_config.csv', csvColumns, rows);
    }, [data]);

    useEffect(() => { loadData(); }, [loadData]);
    useEffect(() => { setCurrentPage(1); }, [filterText]);

    const filteredData = filterText
        ? data.filter(item =>
            Object.values(item).some(v =>
                String(v).toLowerCase().includes(filterText.toLowerCase())
            )
        )
        : data;

    const totalPages = Math.max(1, Math.ceil(filteredData.length / resultsPerPage));
    const paginatedData = filteredData.slice(
        (currentPage - 1) * resultsPerPage,
        currentPage * resultsPerPage,
    );

    if (isLoading) return <Row alignmentHorizontal="center" spacingInset="450"><Loader size="small" /></Row>;

    return (
        <Column spacing="400">
            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}
            {warning && (
                <Alert type="warning" onClose={() => setWarning(null)}>
                    <Column spacing="200">
                        {warning.split('\n').map((line, i) => (
                            <Text key={i} type="b200">{line}</Text>
                        ))}
                    </Column>
                </Alert>
            )}

            <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
            />

            <Row alignmentHorizontal="justify" alignmentVertical="bottom">
                <Text type="h100">{filteredData.length} records</Text>
                <Row spacing="200">
                    <SearchField
                        value={filterText}
                        onChange={setFilterText}
                        onSubmit={() => {}}
                        placeholder="Search..."
                        width={200}
                    />
                    <Button
                        size="small"
                        type="primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                    >
                        {isUploading ? 'Uploading...' : 'Upload CSV'}
                    </Button>
                    <Button size="small" onClick={handleDownload} disabled={data.length === 0}>
                        Download CSV
                    </Button>
                    <Button size="small" onClick={loadData}>Refresh</Button>
                </Row>
            </Row>

            <Table headerRows={1} showDividers>
                <TableRow highlightOnHover={false}>
                    {COLUMNS.map(col => (
                        <TableCell key={col.id}>
                            <Text type="b200" color="secondary">{col.label}</Text>
                        </TableCell>
                    ))}
                </TableRow>
                {paginatedData.map((item, idx) => (
                    <TableRow key={item.composite_key || idx} highlightOnHover>
                        {COLUMNS.map(col => (
                            <TableCell key={col.id}>
                                <Text type="b200">{item[col.id] || '-'}</Text>
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </Table>

            {totalPages > 1 && (
                <Row alignmentHorizontal="center">
                    <Pagination
                        numberOfPages={totalPages}
                        currentPage={currentPage}
                        onChange={setCurrentPage}
                    />
                </Row>
            )}
        </Column>
    );
};

export default NodeConfig;
